import { type ReactNode } from 'react';

import type { ActionSet } from '../../../authz/types';
import { useRoleActionContext } from '../../../context/role-actions';
import type { ACTIONTYPE } from '../../../layout/permissions/actions';

interface CanBase {
	children: ReactNode;
	fallback?: ReactNode;
}

/**
 * Exactly one predicate, enforced by the type system.
 *
 * The union shape exists because the previous signature made all three optional and
 * defaulted to *allowed* when none was supplied — so `<Can>` with a typo'd prop name,
 * or `anyOf={[]}` from an empty array, rendered a privileged control to everyone while
 * looking exactly like a permission check. A gate that silently opens is worse than no
 * gate, because it stops anyone from noticing the gate is missing.
 */
type CanProps = CanBase &
	(
		| { action: ACTIONTYPE; anyOf?: never; allOf?: never }
		| { anyOf: ActionSet; action?: never; allOf?: never }
		| { allOf: ActionSet; action?: never; anyOf?: never }
	);

/**
 * Renders children only when the current principal holds the required action(s).
 *
 *   <Can action={ACTIONS.SYSTEM_MANAGEMENT_USERS_DELETE}>
 *     <DeleteUserButton />
 *   </Can>
 *
 * `anyOf` is OR, `allOf` is AND. While permissions are still unresolved the gate stays
 * OPEN — see `authz/types.ts`; an unresolved set usually means the session lapsed, and
 * hiding controls there looks like revoked access rather than an expired login.
 */
const Can = ({ action, anyOf, allOf, children, fallback = null }: CanProps) => {
	const { can, canAny, canAll, isResolved } = useRoleActionContext();

	if (!isResolved) return <>{children}</>;

	const allowed = allOf ? canAll(allOf) : anyOf ? canAny(anyOf) : can(action);

	return <>{allowed ? children : fallback}</>;
};

export default Can;
